import React, { useState } from 'react';
import { Shield, User, MapPin, Edit2, Activity, TrendingUp, Package, Truck, DollarSign, FileText, History, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getActionsForStatus, getQuickActionsForStatus } from './WorkflowActionMapper';
import { logger } from '../../utils/logger';

/**
 * Composant Actions Administratives
 * Basé sur le mockup fourni
 */

const ActionsAdministratives = ({ 
  operation, 
  userRole,
  onStatusChange,
  onAssignOperator,
  onAssignFieldOps,
  onEditDetails,
  onViewHistory,
  onCancelOperation,
  onQuickAction
}) => {
  const [showSecondaryActions, setShowSecondaryActions] = useState(false);
  
  // Récupérer les actions pour le statut actuel
  const actions = getActionsForStatus(operation.status, userRole);
  const quickActions = getQuickActionsForStatus(operation.status);
  
  // Icônes pour les actions rapides
  const quickActionIcons = {
    planifier: <Activity className="w-5 h-5" />,
    chargement: <Package className="w-5 h-5" />,
    livraison: <Truck className="w-5 h-5" />,
    facturation: <DollarSign className="w-5 h-5" />
  };

  const handlePrimaryAction = () => {
    if (actions.primary.status) {
      onStatusChange(actions.primary.status, actions.primary.requires_modal);
    } else if (actions.primary.action) {
      // Action custom (ex: validation devis client)
      onStatusChange(actions.primary.action, actions.primary.requires_modal);
    }
  };

  const handleSecondaryAction = (action) => {
    switch (action.action) {
      case 'edit_details':
        onEditDetails();
        break;
      case 'view_history':
        onViewHistory();
        break;
      case 'cancel':
        onCancelOperation();
        break;
      default:
        logger.debug('Action:', action);
    }
  };

  // Obtenir la couleur du bouton principal
  const getPrimaryButtonClass = () => {
    const color = actions.primary?.color || 'primary';
    switch (color) {
      case 'purple':
        return 'bg-purple-600 hover:bg-purple-700 text-white';
      case 'success':
        return 'bg-emerald-600 hover:bg-emerald-700 text-white';
      case 'warning':
        return 'bg-orange-600 hover:bg-orange-700 text-white';
      default:
        return 'bg-blue-600 hover:bg-blue-700 text-white';
    }
  };

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <Shield className="w-6 h-6" />
          Actions Administratives
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Section Responsables */}
        <div className="bg-white rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-gray-900 mb-4">Responsables</h3>
          
          {/* Opérateur */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-700">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Opérateur</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onAssignOperator}
                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Changer
              </Button>
            </div>
            
            {operation.assignedOperatorId ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold">
                  {operation.assignedOperatorName?.substring(0, 2).toUpperCase() || 'OP'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {operation.assignedOperatorName || 'Opérateur assigné'}
                  </p>
                  <p className="text-sm text-emerald-700">
                    {operation.assignedOperatorJobTitle || 'Poste non renseigné'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-500 italic">Non assigné</p>
              </div>
            )}
          </div>
          
          {/* Field Ops */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Field Ops</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onAssignFieldOps}
                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {operation.assignedFieldOpsId ? 'Changer' : 'Assigner'}
              </Button>
            </div>
            
            {operation.assignedFieldOpsId ? (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold">
                  {operation.assignedFieldOpsName?.substring(0, 2).toUpperCase() || 'FO'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {operation.assignedFieldOpsName || 'Field Ops assigné'}
                  </p>
                  <p className="text-sm text-cyan-700">
                    {operation.assignedFieldOpsJobTitle || 'Poste non renseigné'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-500 italic">Non assigné</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions Rapides Grid */}
        {quickActions.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.id}
                variant="outline"
                className="h-auto py-4 flex items-center justify-center gap-2 bg-white hover:bg-gray-50 border-gray-200"
                onClick={() => onQuickAction && onQuickAction(action.id)}
              >
                {quickActionIcons[action.id]}
                <span className="font-medium">{action.label}</span>
              </Button>
            ))}
          </div>
        )}

        {/* Action Principale */}
        {actions.primary && (
          <Button
            className={`w-full h-auto py-4 text-lg font-semibold shadow-md ${getPrimaryButtonClass()}`}
            onClick={handlePrimaryAction}
          >
            <FileText className="w-5 h-5 mr-2" />
            {actions.primary.label}
          </Button>
        )}

        {/* Actions Secondaires */}
        {actions.secondary && actions.secondary.length > 0 && (
          <div className="space-y-2">
            {actions.secondary.map((action, index) => {
              // Bouton annuler en rouge
              if (action.action === 'cancel') {
                return (
                  <Button
                    key={index}
                    variant="destructive"
                    className="w-full h-auto py-3 bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => handleSecondaryAction(action)}
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {action.label}
                  </Button>
                );
              }
              
              // Autres actions secondaires
              return (
                <Button
                  key={index}
                  variant="outline"
                  className="w-full h-auto py-3 bg-white hover:bg-gray-50 border-gray-200 text-gray-900"
                  onClick={() => handleSecondaryAction(action)}
                >
                  {action.action === 'edit_details' && <Edit2 className="w-4 h-4 mr-2" />}
                  {action.action === 'view_history' && <History className="w-4 h-4 mr-2" />}
                  {action.label}
                </Button>
              );
            })}
          </div>
        )}

        {/* Message si aucune action */}
        {!actions.primary && (!actions.secondary || actions.secondary.length === 0) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700 text-center">
              ℹ️ Aucune action disponible pour ce statut
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionsAdministratives;